<script setup lang="ts">
import TheLayoutApp from "@/Layouts/TheLayoutApp.vue";
import AppContainer from "@/Components/Shared/container/AppContainer.vue";
import AppPage from "@/Components/Shared/page/AppPage.vue";
import AppTitle from "@/Components/Shared/title/AppTitle.vue";
import AppHeader from "@/Components/Shared/header/AppHeader.vue";
import AppGrid from "@/Components/Shared/grid/AppGrid.vue";
import AppColumn from "@/Components/Shared/grid/AppColumn.vue";
import AppUserGrid from "@/Components/Shared/user/AppUserGrid.vue";
import AppCommentListItem from "@/Components/App/comments/AppCommentListItem.vue";
import AppText from "@/Components/Shared/text/AppText.vue";
import AppLink from "@/Components/Shared/link/AppLink.vue";
import AppCollectionGrid from "@/Components/App/collections/AppCollectionGrid.vue";
import AppPostGrid from "@/Components/App/posts/AppPostGrid.vue";
import AppPageHeader from "@/Components/Shared/page/AppPageHeader.vue";

type Props = {
    popularCollections: object;
    popularPosts: object;
    latestUsers: object;
    popularUsers: object;
    latestComments: object;
};

const {
    popularCollections,
    latestUsers,
    popularUsers,
    latestComments,
    popularPosts,
} = defineProps<Props>();

const breadcrumbs = [
    {
        label: "Community",
        path: "/community",
    },
];
</script>

<template>
    <TheLayoutApp
        title="Community"
        :breadcrumbs="breadcrumbs"
    >
        <AppContainer size="large">
            <AppPage size="small">
                <AppPageHeader title="Community" />
                <AppGrid>
                    <AppColumn class="md:col-span-6">
                        <div class="flex flex-col gap-8">
                            <div>
                                <AppHeader>
                                    <AppTitle
                                        as="h2"
                                        variant="main"
                                    >
                                        Leden
                                    </AppTitle>
                                </AppHeader>

                                <div class="flex flex-col gap-4 sm:gap-8">
                                    <section>
                                        <AppHeader>
                                            <AppTitle
                                                as="h3"
                                                variant="subtitle"
                                            >
                                                Populair
                                            </AppTitle>
                                        </AppHeader>

                                        <AppUserGrid
                                            :users="popularUsers.data"
                                            size="small"
                                            avatar-size="large"
                                        />
                                    </section>

                                    <section>
                                        <AppHeader>
                                            <AppTitle
                                                as="h3"
                                                variant="subtitle"
                                            >
                                                Meest recent
                                            </AppTitle>
                                        </AppHeader>

                                        <AppUserGrid
                                            :users="latestUsers.data"
                                            size="small"
                                            avatar-size="large"
                                        />
                                    </section>
                                </div>
                            </div>

                            <section v-if="popularCollections.data.length">
                                <AppHeader>
                                    <AppTitle
                                        as="h2"
                                        variant="main"
                                    >
                                        Populaire collecties
                                    </AppTitle>
                                </AppHeader>

                                <AppCollectionGrid
                                    :collections="popularCollections.data"
                                    show-author
                                    variant="community"
                                    size=""
                                />
                            </section>
                        </div>
                    </AppColumn>
                    <AppColumn class="md:col-span-6">
                        <section
                            v-if="popularPosts?.data?.length"
                            class="mb-8"
                        >
                            <AppHeader>
                                <AppTitle
                                    as="h2"
                                    variant="main"
                                >
                                    Recente posts
                                </AppTitle>
                            </AppHeader>

                            <AppPostGrid
                                :posts="popularPosts.data"
                                :meta="popularPosts.meta"
                            />
                        </section>
                        <section v-if="latestComments?.data?.length">
                            <AppHeader>
                                <AppTitle
                                    as="h2"
                                    variant="main"
                                >
                                    Recente reacties
                                </AppTitle>
                            </AppHeader>

                            <div class="space-y-3">
                                <div
                                    v-for="comment in latestComments.data"
                                    :key="comment.id"
                                >
                                    <AppCommentListItem
                                        :comment="comment"
                                        :show-link="true"
                                        :hide-children="true"
                                        :hide-options="true"
                                    />

                                    <div
                                        v-if="comment.commentable"
                                        class="px-4 py-2 bg-gray-white"
                                    >
                                        <AppText
                                            size="xxsmall"
                                            color="gray"
                                        >
                                            Reactie op:
                                        </AppText>

                                        <AppLink
                                            v-if="comment.commentable"
                                            :href="comment.commentable.path"
                                            class="text-sm font-medium"
                                        >
                                            <template
                                                v-if="
                                                    comment.commentable.type ===
                                                    'post'
                                                "
                                            >
                                                {{
                                                    comment.commentable.title ||
                                                    "Post"
                                                }}
                                            </template>
                                            <template
                                                v-else-if="
                                                    comment.commentable.type ===
                                                    'review'
                                                "
                                            >
                                                {{
                                                    comment.commentable.title ||
                                                    "Review"
                                                }}
                                                <template
                                                    v-if="
                                                        comment.commentable
                                                            .product
                                                    "
                                                >
                                                    van
                                                    {{
                                                        comment.commentable
                                                            .product.name
                                                    }}
                                                </template>
                                            </template>
                                            <template v-else>
                                                {{
                                                    comment.commentable.title ||
                                                    comment.commentable.type
                                                }}
                                            </template>
                                        </AppLink>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </AppColumn>
                </AppGrid>
            </AppPage>
        </AppContainer>
    </TheLayoutApp>
</template>
